import Fieldset from 'components/_UI/Fieldset';
import Text from 'components/_UI/Text';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import { useForm } from "react-hook-form";
import colors from 'theme/colors';
import { useCheckout } from 'context/checkout/Checkout';
import masks from 'helpers/masks';
import unformat from 'helpers/unformat';
import { useUser } from 'context/user/User';
import fieldValidations from 'helpers/fieldValidations';
import CustomFieldsForm from './CustomFieldsForm';
import { PRODUCT_TYPES } from '../commom/paymentFormUtils';
import { User } from 'lucide-react';

export default function ReceiptForm({
    trigger,
    onReceiptFormCompleted,
    tickets,
    customFields = []
}) {
    const { checkout, setCheckout } = useCheckout();
    const { user } = useUser();

    const customFieldsRefs = useRef([]);
    // O carrinho pode misturar ingressos e produtos avulsos (PRODUCT_TYPES.PRODUCT);
    // só ingresso vira card de participante, então renderização e onSubmit
    // precisam indexar sempre a partir da mesma lista filtrada.
    const ticketsOnly = tickets?.filter(ticket => ticket.type === PRODUCT_TYPES.TICKET) || [];
    const isMultiTicket = ticketsOnly.length > 1;
    // Quando algum ingresso do carrinho já pede CPF individual, o CPF do
    // comprador é dispensado — reaproveitamos o de um dos participantes em
    // vez de perguntar duas vezes.
    const anyTicketRequiresDocument = ticketsOnly.some(ticket => ticket.requires_document);
    const showBuyerDocument = !anyTicketRequiresDocument;

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm();

    useEffect(() => {
        if (trigger) {
            handleSubmit(onSubmit)();
        }
    }, [trigger]);

    useEffect(() => {
        setValue("firstName_0", user?.first_name || "");
        setValue("lastName_0", user?.last_name || "");
        setValue("email_0", user?.email || "");
        setValue("phone", user?.phone || "");
    }, [tickets, setValue, user]);

    function createCustomFieldsPayload(fields, values) {
        const payload = {}

        Object.values(fields).forEach((field) => {
            if (field.type === 'upload') {
                if (values[field.id].url) {
                    payload[field.label] = values[field.id].url
                }
                return
            }

            if (['radio', 'checkbox'].includes(field.type)) {
                if (values[field.id].length) {
                    payload[field.label] = values[field.id]
                }
                return
            }
            if (values[field.id]) {
                payload[field.label] = values[field.id]
            }
        })

        return payload
    }

    function onSubmit(data) {
        const customFieldValues = []

        if (customFieldsRefs.current) {
            for (const ref of customFieldsRefs.current) {
                if (ref.validate()) {
                    ref.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    })
                    return
                }

                const customFieldValue = createCustomFieldsPayload(customFields, ref.getCustomFieldsValues())
                customFieldValues.push(customFieldValue)
            }
        }

        const phone = data.phone || user?.phone || '';
        const customerFirstName = isMultiTicket ? user?.first_name : data["firstName_0"] || user?.first_name;
        const customerLastName = isMultiTicket ? user?.last_name : data["lastName_0"] || user?.last_name;

        const guests = ticketsOnly.map((ticket, index) => ({
            name: `${data[`firstName_${index}`]} ${data[`lastName_${index}`]}`,
            email: data[`email_${index}`],
            batch_id: ticket.id,
            document: ticket.requires_document ? unformat.getNumbers(data[`document_${index}`] || '') : null,
            custom_fields: customFieldValues[index] || null
        }));

        // Sem CPF do comprador na tela, o primeiro CPF de participante
        // disponível cobre o campo `document` do pedido.
        const document = showBuyerDocument
            ? unformat.getNumbers(data.document || '')
            : (guests.find(guest => guest.document)?.document || '');

        const payload = {
            firstName: customerFirstName,
            lastName: customerLastName,
            document,
            phone: unformat.getNumbers(phone),
            guests,
        };

        setCheckout({
            ...checkout,
            customer: {
                first_name: customerFirstName,
                last_name: customerLastName,
                phone,
                guests,
            },
        });

        if (onReceiptFormCompleted) {
            onReceiptFormCompleted(payload);
        }
    }

    return (
        <FormSection>
            <SectionHeader>
                <SectionIcon>
                    <User size={16} color={colors.occasionalPurple} />
                </SectionIcon>
                <HeaderContent>
                    <Text type='bold' paddingBottom={0} color={colors.nightDark}>
                        Informações do participante
                    </Text>
                </HeaderContent>
            </SectionHeader>

            <Column gap='16px'>
                {isMultiTicket ? (
                    <TicketCard>
                        <TicketCardHeader>
                            <TicketBadge>1</TicketBadge>
                            <Text name='small' color={colors.occasionalPurple} type='bold'>
                                Ingresso 1 - {ticketsOnly[0]?.name}
                            </Text>
                        </TicketCardHeader>

                        <FieldGrid>
                            <Column>
                                <Fieldset
                                    label='Nome *'
                                    name="firstName_0"
                                    register={register}
                                    defaultValue={user?.first_name || ""}
                                    validations={fieldValidations.name('Nome')}
                                    layout='squired'
                                />

                                {errors?.firstName_0?.message && (
                                    <Column>
                                        <Text color={colors.danger} name='small'> {errors.firstName_0?.message} </Text>
                                    </Column>
                                )}
                            </Column>

                            <Column>
                                <Fieldset
                                    label='Sobrenome *'
                                    name="lastName_0"
                                    register={register}
                                    defaultValue={user?.last_name || ""}
                                    validations={fieldValidations.name('Sobrenome')}
                                    layout='squired'
                                />
                                {errors?.lastName_0?.message && (
                                    <Column>
                                        <Text color={colors.danger} name='small'> {errors.lastName_0?.message} </Text>
                                    </Column>
                                )}
                            </Column>
                        </FieldGrid>

                        <Column marginTop='12px'>
                            {ticketsOnly[0]?.requires_document ? (
                                <FieldGrid>
                                    <Column>
                                        <Fieldset
                                            label='E-mail *'
                                            name="email_0"
                                            layout='squired'
                                            register={register}
                                            defaultValue={user?.email || ""}
                                            validations={fieldValidations.email('E-mail')}
                                        />
                                        {errors.email_0?.message && (
                                            <Column>
                                                <Text color={colors.danger} name='small'> {errors.email_0?.message} </Text>
                                            </Column>
                                        )}
                                    </Column>

                                    <Column>
                                        <Fieldset
                                            label='CPF *'
                                            name="document_0"
                                            layout='squired'
                                            register={register}
                                            mask={masks.cpf}
                                            validations={fieldValidations.cpf()}
                                        />
                                        {errors.document_0?.message && (
                                            <Column>
                                                <Text color={colors.danger} name='small'> {errors.document_0?.message} </Text>
                                            </Column>
                                        )}
                                    </Column>
                                </FieldGrid>
                            ) : (
                                <>
                                    <Fieldset
                                        label='E-mail *'
                                        name="email_0"
                                        layout='squired'
                                        register={register}
                                        defaultValue={user?.email || ""}
                                        validations={fieldValidations.email('E-mail')}
                                    />
                                    {errors.email_0?.message && (
                                        <Column>
                                            <Text color={colors.danger} name='small'> {errors.email_0?.message} </Text>
                                        </Column>
                                    )}
                                </>
                            )}
                        </Column>

                        {
                            !!customFields.length &&
                            <Column marginTop={'10px'}>
                                <Text name='subtitle' textAlign={'center'} type='bold' marginTop={'16px'}>
                                    Campos adicionais
                                </Text>
                                <CustomFieldsForm
                                    ref={(el) => customFieldsRefs.current[0] = el}
                                    customFields={customFields}
                                />
                            </Column>
                        }
                    </TicketCard>
                ) : (
                    <Column>
                        <FieldGrid>
                            <Column>
                                <Fieldset
                                    marginTop='20px'
                                    label='Nome *'
                                    name="firstName_0"
                                    register={register}
                                    defaultValue={user?.first_name || ""}
                                    validations={fieldValidations.name('Nome')}
                                    layout='squired'
                                />

                                {errors?.firstName_0?.message && (
                                    <Column>
                                        <Text color={colors.danger} name='small'> {errors.firstName_0?.message} </Text>
                                    </Column>
                                )}
                            </Column>

                            <Column>
                                <Fieldset
                                    marginTop='20px'
                                    label='Sobrenome *'
                                    name="lastName_0"
                                    register={register}
                                    defaultValue={user?.last_name || ""}
                                    validations={fieldValidations.name('Sobrenome')}
                                    layout='squired'
                                />
                                {errors?.lastName_0?.message && (
                                    <Column>
                                        <Text color={colors.danger} name='small'> {errors.lastName_0?.message} </Text>
                                    </Column>
                                )}
                            </Column>
                        </FieldGrid>

                        <Column marginTop='6px'>
                            {ticketsOnly?.[0]?.requires_document ? (
                                <FieldGrid>
                                    <Column>
                                        <Fieldset
                                            label='E-mail *'
                                            name="email_0"
                                            layout='squired'
                                            register={register}
                                            defaultValue={user?.email || ""}
                                            validations={fieldValidations.email('E-mail')}
                                        />
                                        {errors.email_0?.message && (
                                            <Column>
                                                <Text color={colors.danger} name='small'> {errors.email_0?.message} </Text>
                                            </Column>
                                        )}
                                    </Column>

                                    <Column>
                                        <Fieldset
                                            label='CPF *'
                                            name="document_0"
                                            layout='squired'
                                            register={register}
                                            mask={masks.cpf}
                                            validations={fieldValidations.cpf()}
                                        />
                                        {errors.document_0?.message && (
                                            <Column>
                                                <Text color={colors.danger} name='small'> {errors.document_0?.message} </Text>
                                            </Column>
                                        )}
                                    </Column>
                                </FieldGrid>
                            ) : (
                                <>
                                    <Fieldset
                                        label='E-mail *'
                                        name="email_0"
                                        layout='squired'
                                        register={register}
                                        defaultValue={user?.email || ""}
                                        validations={fieldValidations.email('E-mail')}
                                    />
                                    {errors.email_0?.message && (
                                        <Column>
                                            <Text color={colors.danger} name='small'> {errors.email_0?.message} </Text>
                                        </Column>
                                    )}
                                </>
                            )}
                        </Column>

                        {
                            !!customFields.length &&
                            <Column marginTop={'10px'}>
                                <Text name='subtitle' textAlign={'center'} type='bold' marginTop={'16px'}>
                                    Campos adicionais
                                </Text>
                                <CustomFieldsForm
                                    ref={(el) => customFieldsRefs.current[0] = el}
                                    customFields={customFields}
                                />
                            </Column>
                        }
                    </Column>
                )}

                {
                    isMultiTicket &&
                    ticketsOnly.slice(1).map((ticket, index) => (
                        <TicketCard key={index + 1}>
                            <TicketCardHeader>
                                <TicketBadge>{index + 2}</TicketBadge>
                                <Text name='small' color={colors.occasionalPurple} type='bold'>
                                    Ingresso {index + 2} - {ticket.name}
                                </Text>
                            </TicketCardHeader>

                            <FieldGrid>
                                <Column>
                                    <Fieldset
                                        label='Nome *'
                                        name={`firstName_${index + 1}`}
                                        register={register}
                                        defaultValue={ticket.firstName || ""}
                                        validations={fieldValidations.name('Nome')}
                                        layout='squired'
                                    />

                                    {errors[`firstName_${index + 1}`]?.message && (
                                        <Column>
                                            <Text color={colors.danger} name='small'> {errors[`firstName_${index + 1}`]?.message} </Text>
                                        </Column>
                                    )}
                                </Column>

                                <Column>
                                    <Fieldset
                                        label='Sobrenome *'
                                        name={`lastName_${index + 1}`}
                                        register={register}
                                        defaultValue={ticket.lastName || ""}
                                        validations={fieldValidations.name('Sobrenome')}
                                        layout='squired'
                                    />
                                    {errors[`lastName_${index + 1}`]?.message && (
                                        <Column>
                                            <Text color={colors.danger} name='small'> {errors[`lastName_${index + 1}`]?.message} </Text>
                                        </Column>
                                    )}
                                </Column>
                            </FieldGrid>
                            <Column marginTop='12px'>
                                {ticket.requires_document ? (
                                    <FieldGrid>
                                        <Column>
                                            <Fieldset
                                                label='E-mail *'
                                                name={`email_${index + 1}`}
                                                layout='squired'
                                                register={register}
                                                validations={fieldValidations.email('E-mail')}
                                            />
                                            {errors[`email_${index + 1}`]?.message && (
                                                <Column>
                                                    <Text color={colors.danger} name='small'> {errors[`email_${index + 1}`]?.message} </Text>
                                                </Column>
                                            )}
                                        </Column>

                                        <Column>
                                            <Fieldset
                                                label='CPF *'
                                                name={`document_${index + 1}`}
                                                layout='squired'
                                                register={register}
                                                mask={masks.cpf}
                                                validations={fieldValidations.cpf()}
                                            />
                                            {errors[`document_${index + 1}`]?.message && (
                                                <Column>
                                                    <Text color={colors.danger} name='small'> {errors[`document_${index + 1}`]?.message} </Text>
                                                </Column>
                                            )}
                                        </Column>
                                    </FieldGrid>
                                ) : (
                                    <>
                                        <Fieldset
                                            label='E-mail *'
                                            name={`email_${index + 1}`}
                                            layout='squired'
                                            register={register}
                                            validations={fieldValidations.email('E-mail')}
                                        />
                                        {errors[`email_${index + 1}`]?.message && (
                                            <Column>
                                                <Text color={colors.danger} name='small'> {errors[`email_${index + 1}`]?.message} </Text>
                                            </Column>
                                        )}
                                    </>
                                )}
                            </Column>

                            {
                                !!customFields.length &&
                                <Column marginTop={'10px'}>
                                    <Text name='subtitle' textAlign={'center'} type='bold' marginTop={'16px'}>
                                        Campos adicionais
                                    </Text>
                                    <CustomFieldsForm
                                        ref={(el) => customFieldsRefs.current[index + 1] = el}
                                        customFields={customFields}
                                    />
                                </Column>
                            }
                        </TicketCard>
                    ))
                }

                <Column>
                    {isMultiTicket && (
                        <Text name='small' color={colors.night} type='bold' marginBottom='4px'>
                            Dados do comprador
                        </Text>
                    )}

                    <Fieldset
                        marginTop='6px'
                        label='Telefone *'
                        name="phone"
                        register={register}
                        defaultValue={user?.phone || ""}
                        mask={masks.phone}
                        validations={fieldValidations.phone()}
                        layout='squired'
                    />

                    {errors?.phone?.message && (
                        <Column marginLeft='10px'>
                            <Text color={colors.danger} name='small'> {errors.phone?.message} </Text>
                        </Column>
                    )}
                </Column>

                {
                    showBuyerDocument &&
                    <Column>
                        <Fieldset
                            marginTop='6px'
                            label='CPF *'
                            layout='squired'
                            name="document"
                            register={register}
                            mask={masks.cpf}
                            validations={fieldValidations.cpf()}
                        />

                        {errors.document?.message && (
                            <Column>
                                <Text color={colors.danger} name='small'> {errors.document?.message} </Text>
                            </Column>
                        )}
                    </Column>
                }

            </Column>
        </FormSection>
    );
}

const FormSection = styled.div`
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 24px 20px;
    margin-top: 24px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 28px 28px;
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
`;

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`;

const HeaderContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const TicketCard = styled(Column)`
    background: ${colors.neutral};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 16px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 20px;
    }
`;

const TicketCardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
`;

const TicketBadge = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
`;

const FieldGrid = styled.div`
    display: grid;
    gap: 12px;
    grid-template-columns: 1fr;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: 1fr 1fr;
    }
`;
